import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { toast } from "react-toastify";

import type { StepperContextValue } from "@/context";
import { loginService } from "@/services/login";
import type { ApiEnvelope } from "@/types/api";
import { redirect } from "@/utils/helpers";

interface OtpData {
  phoneConfirm: string;
  systemUserId: string | number;
  userName: string;
  password: string;
}

interface OtpResult {
  code?: string;
  description?: string;
  type?: string;
  message?: string;
}

interface SecurityImageResult {
  imageExists?: boolean;
}

export interface LoginResult {
  nexusToken?: string;
  TokenExpireInSeconds?: number;
  description?: string;
  [key: string]: unknown;
}

interface CheckOtpStore {
  data: LoginResult | null;
  error: unknown;
  loading: boolean;
  otpVerified: boolean;
  checkOtp: (otpData: OtpData, stepper: Pick<StepperContextValue, "moveNext">) => Promise<void>;
}

export const useCheckOtpStore = create<CheckOtpStore>()(
  immer((set) => ({
    data: null,
    error: null,
    loading: false,
    otpVerified: false,
    checkOtp: async (otpData, stepper) => {
      set({
        data: null,
        error: null,
        loading: true,
        otpVerified: false,
      });

      try {
        const { data: otpResponse } = await loginService.checkOtp<ApiEnvelope<OtpResult>>({
          otpCode: otpData.phoneConfirm,
          systemUserId: otpData.systemUserId,
        });

        if (!otpResponse?.body || otpResponse.body.code !== "11") {
          toast.error(otpResponse?.body?.description ?? "OTP doğrulaması başarısız");
          set({
            error: otpResponse?.body ?? true,
            loading: false,
            otpVerified: false,
          });

          return;
        }

        set({ otpVerified: true });

        const securityImageResponse = await loginService.getSecurityUserImage<
          ApiEnvelope<SecurityImageResult>
        >({
          systemUserId: otpData.systemUserId,
        });

        if (!securityImageResponse?.data?.body?.imageExists) {
          set({ loading: false });
          stepper.moveNext();

          return;
        }

        const loginResponse = await loginService.login<ApiEnvelope<LoginResult>>(
          {
            userName: otpData.userName,
            password: otpData.password,
          },
          {
            "x-Nexus-Otpcode": otpData.phoneConfirm,
          }
        );
        const { header, body } = loginResponse.data || {};

        if (!header?.isSuccess || !body?.nexusToken) {
          toast.error(body?.description ?? "Login başarısız");
          set({
            error: loginResponse.data,
            loading: false,
            otpVerified: false,
          });

          return;
        }

        await fetch("/api/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            TokenExpireInSeconds: body.TokenExpireInSeconds,
          }),
        });
        set({
          data: body,
          error: null,
          loading: false,
          otpVerified: true,
        });
        redirect({ target: "/" });
      } catch {
        toast.error("OTP doğrulaması başarısız");
        set({
          error: true,
          loading: false,
          otpVerified: false,
        });
      }
    },
  }))
);
